<template>
  <div
    :class="[
      'add-new-popup z-[999] fixed transition-all inset-0 overflow-x-hidden overflow-y-auto lg:py-[20px]',
      { active: isOpen },
    ]"
  >
    <div
      class="popup-dialog flex transition-all max-w-[600px] min-h-full items-center mx-auto"
    >
      <div
        class="modal-card w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden"
      >
        <!-- Header with blue background -->
        <div
          class="modal-header bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between"
        >
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <i class="ri-edit-line text-xl"></i>
            </div>
            <h3 class="text-xl font-semibold">{{ directT('calendar.editEvent') }}</h3>
          </div>
          <button
            type="button"
            class="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
            @click="closeModal"
          >
            <i class="ri-close-line text-lg"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="modal-content p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            <div class="flex items-center space-x-2">
              <i class="ri-error-warning-line text-lg"></i>
              <span>{{ error }}</span>
            </div>
          </div>

          <form @submit.prevent="updateEvent" class="space-y-6">
            <!-- Event Title -->
            <div class="form-group">
              <label class="form-label">
                {{ directT('calendar.title') }}
              </label>
              <input
                type="text"
                v-model="formData.title"
                required
                class="form-input"
                :placeholder="directT('calendar.addTitle')"
              />
            </div>

            <!-- Date and Time Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="form-group">
                <label class="form-label">
                  {{ directT('calendar.startDateTime') }}
                </label>
                <input
                  type="datetime-local"
                  v-model="formData.startTime"
                  required
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label class="form-label">
                  {{ directT('calendar.endDateTime') }}
                </label>
                <input
                  type="datetime-local"
                  v-model="formData.endTime"
                  required
                  class="form-input"
                />
              </div>
            </div>

            <!-- Type and Customer Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="form-group">
                <label class="form-label">
                  {{ directT('calendar.type') }}
                </label>
                <select v-model="formData.eventType" class="form-input">
                  <option value="">{{ directT('calendar.selectType') }}</option>
                  <option value="meeting">{{ directT('calendar.meeting') }}</option>
                  <option value="call">{{ directT('calendar.call') }}</option>
                  <option value="appointment">{{ directT('calendar.appointment') }}</option>
                  <option value="reminder">{{ directT('calendar.reminder') }}</option>
                  <option value="other">{{ directT('calendar.other') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">
                  {{ directT('calendar.customerName') }}
                </label>
                <input
                  type="text"
                  v-model="formData.customerName"
                  class="form-input"
                  :placeholder="directT('calendar.enterName')"
                />
              </div>
            </div>

            <!-- Phone and Location Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="form-group">
                <label class="form-label">
                  {{ directT('calendar.phoneNumber') }}
                </label>
                <input
                  type="tel"
                  v-model="formData.phone"
                  class="form-input"
                  :placeholder="directT('calendar.enterPhoneNumber')"
                />
              </div>
              <div class="form-group">
                <label class="form-label">
                  {{ directT('calendar.location') }}
                </label>
                <input
                  type="text"
                  v-model="formData.location"
                  class="form-input"
                  :placeholder="directT('calendar.enterLocation')"
                />
              </div>
            </div>

            <!-- Description -->
            <div class="form-group">
              <label class="form-label">
                {{ directT('calendar.description') }}
              </label>
              <textarea
                v-model="formData.description"
                class="form-input min-h-[100px] resize-none"
                :placeholder="directT('calendar.enterDescription')"
              ></textarea>
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                type="button"
                class="btn-danger flex-1 sm:flex-none"
                @click="deleteEvent"
                :disabled="isLoading"
              >
                <i class="ri-delete-bin-line text-lg"></i>
                {{ directT('calendar.delete') }}
              </button>
              <div class="flex flex-col sm:flex-row gap-3 flex-1">
                <button
                  type="button"
                  class="btn-secondary flex-1 sm:flex-none"
                  @click="closeModal"
                >
                  <i class="ri-close-line text-lg"></i>
                  {{ directT('calendar.cancel') }}
                </button>
                <button
                  type="submit"
                  class="btn-primary flex-1"
                  :disabled="isLoading"
                >
                  <i class="ri-save-line text-lg"></i>
                  {{ isLoading ? directT('calendar.saving') : directT('calendar.saveChanges') }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, defineExpose } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

const isOpen = ref(false);
const error = ref('');
const isLoading = ref(false);
const eventId = ref('');

// i18n setup
const { locale } = useI18n();

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Direct translation function that bypasses vue-i18n
const directT = (key: string): string => {
  const parts = key.split('.');
  let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
  
  for (const part of parts) {
    if (!result || !(part in result)) {
      console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
      // Fallback to English if translation not found
      let enResult: TranslationObject = translationFiles.en;
      for (const fallbackPart of parts) {
        if (!enResult || !(fallbackPart in enResult)) return key;
        enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
      }
      return typeof enResult === 'string' ? enResult : key;
    }
    result = result[part as keyof typeof result] as TranslationObject;
  }
  return typeof result === 'string' ? result : key;
};

const formData = reactive({
  title: '',
  startTime: '',
  endTime: '',
  eventType: '',
  customerName: '',
  phone: '',
  location: '',
  description: ''
});

// Show modal and load event data
function openModal(event: any) {
  // Format datetime to work with input type datetime-local
  const formatDateForInput = (dateStr: string | Date) => {
    if (!dateStr) return '';
    
    // Convert to string if it's a Date object
    const dateString = typeof dateStr === 'string' 
      ? dateStr 
      : dateStr instanceof Date 
        ? dateStr.toISOString() 
        : String(dateStr);
    
    // Handle ISO date strings
    if (dateString.includes('T')) {
      // Simply extract the YYYY-MM-DDTHH:MM part without any conversion
      // This preserves the exact time for editing
      return dateString.substring(0, 16);
    }
    
    // Fallback for non-ISO strings
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // Set form data from event
  eventId.value = event.id;
  formData.title = event.title;
  formData.startTime = formatDateForInput(event.start);
  formData.endTime = formatDateForInput(event.end);
  formData.eventType = event.extendedProps?.eventType || '';
  formData.customerName = event.extendedProps?.customerName || '';
  formData.phone = event.extendedProps?.phone || '';
  formData.location = event.extendedProps?.location || '';
  formData.description = event.extendedProps?.description || '';
  
  // Open modal
  isOpen.value = true;
}

// Close modal
function closeModal() {
  isOpen.value = false;
  resetFormErrors();
}

// Reset form errors
function resetFormErrors() {
  error.value = '';
}

// Update event
async function updateEvent() {
  try {
    isLoading.value = true;
    error.value = '';

    // Validate required fields
    if (!formData.title || !formData.startTime || !formData.endTime) {
      error.value = 'Please fill in all required fields';
      return;
    }

    // Validate end time is after start time
    if (new Date(formData.endTime) <= new Date(formData.startTime)) {
      error.value = 'End date and time must be after start date and time';
      return;
    }

    // Format dates to store the exact time as entered by user without timezone conversion
    const formatDateForStorage = (dateTimeStr: string) => {
      // Store the exact format the user entered
      // This ensures no timezone shifting happens
      
      // Create a formatted string in the format Supabase expects
      // but ensure the time component exactly matches what the user entered
      const [datePart, timePart] = dateTimeStr.split('T');
      
      // Return a properly formatted string that preserves the exact time
      // Adding Z to explicitly mark it as UTC to prevent browser conversions
      return `${datePart}T${timePart}:00.000Z`;
    };

    // Update event in database
    const { data, error: updateError } = await supabase
      .from('calendar_events')
      .update({
        title: formData.title,
        start_time: formatDateForStorage(formData.startTime),
        end_time: formatDateForStorage(formData.endTime),
        event_type: formData.eventType || null,
        customer_name: formData.customerName || null,
        phone: formData.phone || null,
        location: formData.location || null,
        description: formData.description || null
      })
      .eq('id', eventId.value)
      .select();

    if (updateError) throw updateError;

    // Show success message
    createToast('Event updated successfully', 'success');
    
    // Close modal and notify
    closeModal();
    window.dispatchEvent(new CustomEvent('calendar-event-updated', { detail: data[0] }));
    
  } catch (err: any) {
    console.error('Error updating event:', err);
    error.value = err.message || 'Error updating event';
  } finally {
    isLoading.value = false;
  }
}

// Delete event
async function deleteEvent() {
  if (!confirm('Are you sure you want to delete this event?')) {
    return;
  }

  try {
    isLoading.value = true;
    error.value = '';

    const { error: deleteError } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', eventId.value);

    if (deleteError) throw deleteError;

    // Show success message
    createToast('Event deleted successfully', 'success');
    
    // Close modal and notify
    closeModal();
    window.dispatchEvent(new CustomEvent('calendar-event-deleted', { detail: { id: eventId.value } }));
    
  } catch (err: any) {
    console.error('Error deleting event:', err);
    error.value = err.message || 'Error deleting event';
  } finally {
    isLoading.value = false;
  }
}

// Expose methods to parent component
defineExpose({
  openModal
});
</script>

<style scoped>
.add-new-popup {
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.popup-dialog {
  transform: scale(0.95);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.active .popup-dialog {
  transform: scale(1);
  opacity: 1;
}

.modal-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

:deep(.dark) .modal-card {
  background: #1f2937;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

.modal-header {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8) !important;
  color: white;
  border: none;
}

:deep(.dark) .modal-header {
  background: linear-gradient(135deg, #2563eb, #1e40af) !important;
}

.modal-header h3 {
  color: white !important;
  font-weight: 600;
  margin: 0;
}

.modal-content {
  background: #ffffff;
}

:deep(.dark) .modal-content {
  background: #1f2937;
}

/* Form Styles */
.form-group {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

:deep(.dark) .form-label {
  color: #d1d5db;
}

.form-input {
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  color: #1f2937;
  font-size: 14px;
  transition: all 0.2s ease;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

:deep(.dark) .form-input {
  background: #374151;
  border-color: #4b5563;
  color: #f9fafb;
}

:deep(.dark) .form-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
}

:deep(.dark) .form-input::placeholder {
  color: #6b7280;
}

/* Textarea specific */
textarea.form-input {
  height: auto;
  min-height: 100px;
  resize: vertical;
  padding-top: 12px;
  padding-bottom: 12px;
}

/* Select specific */
select.form-input {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 40px;
  appearance: none;
}

/* Button Styles */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb, #1e40af);
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
}

.btn-secondary:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

:deep(.dark) .btn-secondary {
  background: #374151;
  color: #d1d5db;
  border-color: #4b5563;
}

:deep(.dark) .btn-secondary:hover {
  background: #4b5563;
  border-color: #6b7280;
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
}

.btn-danger:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
}

.btn-danger:active {
  transform: translateY(0);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Error Message */
.bg-red-50 {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 12px;
}

:deep(.dark) .bg-red-50 {
  background: #7f1d1d;
  border-color: #991b1b;
  color: #fca5a5;
}

/* Icons */
i {
  line-height: 1;
}

/* Close button in header */
.modal-header button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-header button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Header icon */
.modal-header .w-10 {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .add-new-popup {
    padding: 0;
    align-items: flex-end;
  }

  .popup-dialog {
    max-width: 100%;
    margin: 0;
    min-height: auto;
  }

  .modal-card {
    border-radius: 20px 20px 0 0;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-content {
    padding: 20px;
    max-height: calc(95vh - 80px);
  }

  .grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .form-input {
    font-size: 16px; /* Prevents zoom on iOS */
  }

  .flex.flex-col.sm\\:flex-row {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary,
  .btn-danger {
    width: 100%;
    justify-content: center;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .popup-dialog {
    max-width: 90%;
    margin: 20px auto;
  }
}

/* Animation for form elements */
.form-group {
  animation: slideUp 0.3s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.15s; }
.form-group:nth-child(3) { animation-delay: 0.2s; }
.form-group:nth-child(4) { animation-delay: 0.25s; }
.form-group:nth-child(5) { animation-delay: 0.3s; }
.form-group:nth-child(6) { animation-delay: 0.35s; }

/* Loading state */
.btn-primary:disabled,
.btn-danger:disabled {
  position: relative;
  overflow: hidden;
}

.btn-primary:disabled::after,
.btn-danger:disabled::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* Scrollbar */
.modal-content::-webkit-scrollbar {
  width: 6px;
}

.modal-content::-webkit-scrollbar-track {
  background: transparent;
}

.modal-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

:deep(.dark) .modal-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}

/* Focus states for accessibility */
.form-input:focus,
.btn-primary:focus,
.btn-secondary:focus,
.btn-danger:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Better touch targets for mobile */
@media (max-width: 768px) {
  .form-input,
  .btn-primary,
  .btn-secondary,
  .btn-danger {
    min-height: 48px;
  }
}
</style> 
